import { Form, Formik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { IoFilterSharp } from "react-icons/io5";
import { LiaTimesSolid } from "react-icons/lia";
import { toast } from "react-toastify";
import * as yup from "yup";
import { ERROR_MSG, ROLE } from "../../../constants/common";
import {
    RESET_LOADER,
    SET_ERROR,
    SET_LOADER,
} from "../../../constants/contexConstant";
import {
    GET_ADMIN_REMINDER_API,
    GET_HYPERSROT_MEMBERS_API,
    GET_SURVEY_VISIT_LIST,
    RESOLVE_REMINDER_SALES_ENDPOINT,
} from "../../../constants/routes";

import ModalAlt from "../../../components/modal/ModalAlt";
import Pagination from "../../../components/pagination";
import { useContextState } from "../../../context/ContextProvider";
import { checkIsAllowed } from "../../../helpers/helpers";
import cn from "../../../lib/cn";
import request from "../../../services/request";
import Container from "../../../shared/Container";
import P from "../../../shared/typography/P";
import Button from "../../../shared/ui/Button";
import BottomDrawer from "../../../shared/ui/Drawer/BottomDrawer";
import LabeledFormikSelect from "../../../shared/ui/Form/LabeledFormikSelect";
import LinedTab from "../../../shared/ui/tab/LinedTab";
import ReminderCard from "../reminders/ReminderCard";
import VisitsCard from "./VisitsCard";

const filterButtonStyle =
    "bg-gray-200 px-1 py-0.2 text-sm rounded flex items-center gap-0.5 ";

const BellReminders = () => {
    const [currentTab, setCurrentTab] = useState(0);
    const [filterOptions, setFilterOptions] = useState({
        duration: "",
        assignee: "",
    });
    const [reminderPage, setReminderPage] = useState(0);
    const [visitPage, setVisitPage] = useState(0);
    const [reminderPagination, setReminderPagination] = useState(null);
    const [visitPagination, setVisitPagination] = useState(null);

    const [isOpen, setIsOpen] = useState(false);
    const [reminderList, setReminderList] = useState([]);
    const [visitList, setVisitList] = useState([]);
    const { state, dispatch } = useContextState();
    let [assignees, setAssignees] = useState([]);
    const [showAssign, setShowAssign] = useState(null);

    const isAdmin = checkIsAllowed(state?.user?.roles, [ROLE.SUPER_ADMIN]);

    useEffect(() => {
        const getMembers = async () => {
            try {
                dispatch({ type: SET_LOADER });
                let res = await request.authGet({
                    endpoint: GET_HYPERSROT_MEMBERS_API,
                });
                let response = res?.data?.members;
                if (response) {
                    setAssignees(
                        response.map((item) => ({
                            label: item.name,
                            value: item.id,
                        }))
                    );
                }
                dispatch({ type: RESET_LOADER });
            } catch (error) {
                dispatch({ type: RESET_LOADER });
                dispatch({
                    type: SET_ERROR,
                    payload: typeof error === "string" ? error : ERROR_MSG,
                });
            }
        };

        if (isAdmin) {
            getMembers();
        }
    }, [dispatch, isAdmin]);

    const getAllReminders = useCallback(
        async (duration = null, assignee = null, page = 0) => {
            let params = new URLSearchParams();
            if (duration) params.append("duration", duration);
            if (assignee) params.append("assigneeId", assignee);
            params.append("pageNumber", page);

            let queryString = params.toString() ? `&${params.toString()}` : "";

            try {
                dispatch({ type: SET_LOADER });
                const res = await request.authGet({
                    endpoint: `${GET_ADMIN_REMINDER_API}?isForToday=false${queryString}`,
                });
                setReminderList(res?.data?.reminders);
                setReminderPagination({
                    isFirst: res.data.isFirst,
                    isLast: res.data.isLast,
                    pageNumber: res.data.pageNumber,
                    pageCount: res.data.totalPages,
                });
                dispatch({ type: RESET_LOADER });
            } catch (error) {
                dispatch({ type: RESET_LOADER });
                dispatch({
                    type: SET_ERROR,
                    payload: typeof error === "string" ? error : ERROR_MSG,
                });
            }
        },
        [dispatch]
    );

    const getSurveyVisitList = useCallback(
        async (duration = null, assignee = null, page = 0) => {
            let params = new URLSearchParams();
            if (duration) params.append("duration", duration);
            if (assignee) params.append("assigneeId", assignee);
            params.append("page", page);

            let queryString = params.toString() ? `?${params.toString()}` : "";

            try {
                dispatch({ type: SET_LOADER });
                const res = await request.authGet({
                    endpoint: `${GET_SURVEY_VISIT_LIST}${queryString}`,
                });
                setVisitList(res?.data?.surveyVisits);
                setVisitPagination({
                    isFirst: res.data.isFirst,
                    isLast: res.data.isLast,
                    pageNumber: res.data.pageNumber,
                    pageCount: res.data.totalPages,
                });
                dispatch({ type: RESET_LOADER });
            } catch (error) {
                dispatch({ type: RESET_LOADER });
                dispatch({
                    type: SET_ERROR,
                    payload: typeof error === "string" ? error : ERROR_MSG,
                });
            }
        },
        [dispatch]
    );

    useEffect(() => {
        getAllReminders(null, null, 0);
        getSurveyVisitList(null, null, 0);
    }, []);
    const onReminderPageChange = (selectedPage) => {
        setReminderPage(selectedPage);
        getAllReminders(
            filterOptions.duration,
            filterOptions.assignee,
            selectedPage?.selected
        );
    };

    const onVisitPageChange = (selectedPage) => {
        setVisitPage(selectedPage);

        getSurveyVisitList(
            filterOptions.duration,
            filterOptions.assignee,
            selectedPage?.selected
        );
    };

    const handleTabClick = (val) => {
        setCurrentTab(val);
    };

    const handleSubmit = (values, actions) => {
        setIsOpen(false);
        getAllReminders(values.duration, values.assignee);
        getSurveyVisitList(values.duration, values.assignee);
        setFilterOptions(values);
    };

    const handleResolve = async (id) => {
        try {
            dispatch({ type: SET_LOADER });

            await request.authPut({
                endpoint: `${RESOLVE_REMINDER_SALES_ENDPOINT}/${id}`,
            });

            setReminderList(
                reminderList.map((item) =>
                    item.id === id ? { ...item, status: "RESOLVED" } : item
                )
            );
            toast.success("Reminder resolved");
            dispatch({ type: RESET_LOADER });
        } catch (error) {
            dispatch({ type: RESET_LOADER });
            dispatch({
                type: SET_ERROR,
                payload: typeof error === "string" ? error : ERROR_MSG,
            });
        }
    };

    const clearFilters = (key) => {
        let newFilters = { ...filterOptions };
        newFilters[key] = "";
        setFilterOptions(newFilters);
        getAllReminders(newFilters.duration, newFilters.assignee);
        getSurveyVisitList(newFilters.duration, newFilters.assignee);
    };

    /* @TODO => update @habib610 Tue May 20,2025 */
    const handleTicketAssign = (values, actions) => {
        setShowAssign(null);
    };

    return (
        <div className="lg:w-full">
            <Container className="box-border flex-1 lg:min-w-full flex flex-col overflow-hidden">
                <div className="lg:w-full">
                    <div className="flex items-center lg:w-full  gap-2 border-b border-b-blue-200 mb-1.5">
                        <LinedTab
                            className="pb-0  mt-0 pt-0 w-full justify-start"
                            activeClass={
                                "text-blue font-medium duration-300 w-[100px] lg:w-full"
                            }
                            inActiveClass={"text-gray-370 w-[100px] lg:w-full"}
                            data={["Visits", "All Reminders"]}
                            activeTab={currentTab}
                            onClick={handleTabClick}
                        />

                        <button
                            onClick={() => setIsOpen((prev) => !prev)}
                            className="text-xxl"
                        >
                            <IoFilterSharp />
                        </button>
                    </div>
                    <div
                        className={cn("flex gap-1", {
                            "pb-1.5":
                                filterOptions.assignee ||
                                filterOptions.duration,
                        })}
                    >
                        {filterOptions.assignee && (
                            <button
                                onClick={() => clearFilters("assignee")}
                                className={filterButtonStyle}
                            >
                                {
                                    assignees.find(
                                        (item) =>
                                            item.value ===
                                            filterOptions.assignee
                                    ).label
                                }
                                <LiaTimesSolid />
                            </button>
                        )}
                        {filterOptions.duration && (
                            <button
                                onClick={() => clearFilters("duration")}
                                className={filterButtonStyle}
                            >
                                {filterOptions.duration}
                                <LiaTimesSolid />
                            </button>
                        )}
                    </div>
                </div>

                <div className=" flex-1 overflow-auto  text-base no-scrollbar scroll-smooth pb-6 lg:pb-0">
                    <div>
                        {currentTab === 1 ? (
                            <>
                                {reminderList?.length > 0 ? (
                                    reminderList
                                        .sort((a, b) => b?.date - a?.date)
                                        .map((reminder, index) => (
                                            <React.Fragment key={index}>
                                                <ReminderCard
                                                    onClick={handleResolve}
                                                    {...reminder}
                                                />
                                                {index !==
                                                    reminderList.length - 1 && (
                                                    <hr className=" hidden lg:block my-2 border-blue-300" />
                                                )}
                                            </React.Fragment>
                                        ))
                                ) : (
                                    <div className="w-full flex justify-center items-center">
                                        <P className="text-gray-120 font-semibold">
                                            No Reminders Available
                                        </P>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {visitList?.length > 0 ? (
                                    visitList
                                        .sort((a, b) => b?.date - a?.date)
                                        .map((visit, index) => (
                                            <React.Fragment key={index}>
                                                <VisitsCard {...visit} />
                                                {index !==
                                                    visitList.length - 1 && (
                                                    <hr className=" hidden lg:block my-2 border-blue-300" />
                                                )}
                                            </React.Fragment>
                                        ))
                                ) : (
                                    <div className="w-full flex justify-center items-center">
                                        <P className="text-gray-120 font-semibold">
                                            No Visits Available
                                        </P>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                {currentTab === 1 && reminderPagination && (
                    <div className="sticky bottom-0 z-10 bg-white p-1">
                        <Pagination
                            isFirst={reminderPagination.isFirst}
                            isLast={reminderPagination.isLast}
                            pageCount={reminderPagination.pageCount}
                            onChange={onReminderPageChange}
                            forcePage={reminderPagination.pageNumber}
                        />
                    </div>
                )}

                {currentTab === 0 && visitPagination && (
                    <div className="sticky bottom-0 z-10 bg-white p-1">
                        <Pagination
                            isFirst={visitPagination.isFirst}
                            isLast={visitPagination.isLast}
                            pageCount={visitPagination.pageCount}
                            onChange={onVisitPageChange}
                            forcePage={visitPagination.pageNumber}
                        />
                    </div>
                )}
            </Container>

            <div className="lg:hidden">
                <BottomDrawer
                    isOpen={isOpen}
                    onClick={() => setIsOpen((prev) => !prev)}
                    contentClass="min-h-[60vh] rounded-t-4 bg-white"
                    childrenClassName="bg-white"
                    className="z-[103]"
                >
                    <Container>
                        <Formik
                            initialValues={filterOptions}
                            validationSchema={yup.object().shape({
                                duration: yup.string(),
                                assignee: yup.string(),
                            })}
                            onSubmit={handleSubmit}
                        >
                            {({}) => (
                                <Form>
                                    <div className="  pt-2 pb-8  flex flex-col gap-y-2 ">
                                        <P className="text-primary text-sm font-vietnam">
                                            Filter By:
                                        </P>

                                        <LabeledFormikSelect
                                            name="duration"
                                            label="Date Range"
                                            options={filterDuration}
                                        />
                                        {isAdmin && (
                                            <LabeledFormikSelect
                                                name="assignee"
                                                label="Assignee Name"
                                                options={assignees}
                                            />
                                        )}
                                        <Button
                                            type="submit"
                                            className="bg-primary"
                                        >
                                            Apply Filter
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Container>
                </BottomDrawer>
                <BottomDrawer
                    isOpen={showAssign}
                    onClick={() => setShowAssign(false)}
                    contentClass="min-h-[60vh] rounded-t-4 bg-white"
                    childrenClassName="bg-white"
                    className="z-[104]"
                >
                    <Container>
                        <Formik
                            initialValues={filterOptions}
                            validationSchema={yup.object().shape({
                                duration: yup.string(),
                                assignee: yup.string(),
                            })}
                            onSubmit={handleTicketAssign}
                        >
                            {({}) => (
                                <Form>
                                    <div className="  pt-2 pb-8  flex flex-col gap-y-2 ">
                                        {isAdmin && (
                                            <LabeledFormikSelect
                                                name="assignee"
                                                label="Ticket Assign To"
                                                options={assignees}
                                            />
                                        )}
                                        <Button
                                            type="submit"
                                            className="bg-primary"
                                        >
                                            Update Details
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Container>
                </BottomDrawer>
            </div>
            <div className="hidden lg:block">
                <ModalAlt
                    isOpen={isOpen}
                    backgroundColor="white"
                    contentClass={"bg-white"}
                    onClose={() => {
                        setIsOpen((prev) => !prev);
                    }}
                    innerTimes={false}
                    topPadding={"20px"}
                >
                    <Container>
                        <Formik
                            initialValues={filterOptions}
                            validationSchema={yup.object().shape({
                                duration: yup.string(),
                                assignee: yup.string(),
                            })}
                            onSubmit={handleSubmit}
                        >
                            {({}) => (
                                <Form>
                                    <div className="  pt-2 pb-8  flex flex-col gap-y-2 ">
                                        <P className="text-primary text-sm font-vietnam">
                                            Filter By:
                                        </P>

                                        <LabeledFormikSelect
                                            name="duration"
                                            label="Date Range"
                                            options={filterDuration}
                                        />
                                        {isAdmin && (
                                            <LabeledFormikSelect
                                                name="assignee"
                                                label="Assignee Name"
                                                options={assignees}
                                            />
                                        )}
                                        <Button
                                            type="submit"
                                            className="bg-primary"
                                        >
                                            Apply Filter
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Container>
                </ModalAlt>
            </div>
            <div className="hidden lg:block">
                <ModalAlt
                    isOpen={showAssign}
                    contentClass={"bg-white"}
                    onClose={() => {
                        setShowAssign(false);
                    }}
                    innerTimes={false}
                    topPadding={"20px"}
                >
                    <Container>
                        <Formik
                            initialValues={filterOptions}
                            validationSchema={yup.object().shape({
                                duration: yup.string(),
                                assignee: yup.string(),
                            })}
                            onSubmit={handleTicketAssign}
                        >
                            {({}) => (
                                <Form>
                                    <div className="  pt-2 pb-8  flex flex-col gap-y-2 ">
                                        {isAdmin && (
                                            <LabeledFormikSelect
                                                name="assignee"
                                                label="Ticket Assign To"
                                                options={assignees}
                                            />
                                        )}
                                        <Button
                                            type="submit"
                                            className="bg-primary"
                                        >
                                            Update Details
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Container>
                </ModalAlt>
            </div>
        </div>
    );
};

export default BellReminders;

let filterDuration = [
    {
        label: "Today",
        value: "1D",
    },
    {
        label: "7 Days",
        value: "7D",
    },
    {
        label: "30 Days",
        value: "30D",
    },
    {
        label: "1 Year",
        value: "365D",
    },
    {
        label: "Lifetime",
        value: "lifetime",
    },
];
